import React, { useMemo, useState } from 'react';
import {
  EXCEPTION_REASONS,
  EvvExceptionReason,
  EvvReconciliationRunDetail,
  EvvReconciliationService,
  EvvRunItem,
  bucketLabel
} from '../../../../services/evv/reconciliation.service';
import EvvOverrideForm from './EvvOverrideForm';
import { errorMessage, formatMinutes, formatTime } from './evvUi';

interface EvvRunItemsProps {
  run: EvvReconciliationRunDetail;
  canWrite: boolean;
  canOverride: boolean;
  onChanged: (message: string) => void;
}

const REASON_TEXT: Record<string, string> = {
  NO_MATCHING_VISIT: 'No Pelora visit matches this HHAX row.',
  PATIENT_NOT_IDENTIFIED: 'The patient could not be identified (check User Field 1 or import HHAX patient IDs).',
  TIE: 'Two Pelora visits match equally well. Pick the right one.',
  VISIT_HAS_EXCEPTION: 'The matching visit already has an EVV exception set by a coordinator.',
};

function HhaxSummary({ item }: { item: EvvRunItem }) {
  const row = item.hhaxRow;
  if (!row) return null;
  return (
    <p className="text-xs text-slate-600">
      HHAX {row.date} · {formatTime(row.clockIn)}–{formatTime(row.clockOut)} · {formatMinutes(row.durationMinutes)}
      {row.billingServiceCode ? ` · ${row.billingServiceCode}` : ''}
      {row.isSplitShift ? ' · overnight (2 rows)' : ''}
      {row.rowNumbers?.length ? ` · CSV row ${row.rowNumbers.join(', ')}` : ''}
    </p>
  );
}

/** Action items (EVV-12/13) and the matched list (EVV-11: both durations shown). */
const EvvRunItems: React.FC<EvvRunItemsProps> = ({ run, canWrite, canOverride, onChanged }) => {
  const [reason, setReason] = useState<Record<string, EvvExceptionReason | ''>>({});
  const [note, setNote] = useState<Record<string, string>>({});
  const [linkVisit, setLinkVisit] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showMatched, setShowMatched] = useState(false);

  const open = run.unresolvedItems;
  const matched = run.items.filter((i) => ['MATCHED', 'DURATION_MISMATCH', 'HHAX_UNVERIFIED', 'INFORMATIONAL'].includes(i.bucket));
  // Visit picker for HHAX-only rows: Pelora visits of this run that have no HHAX row
  const peloraOnly = useMemo(() => run.items.filter((i) => i.bucket === 'BLACKBIRD_ONLY' && !i.resolution && i.peloraVisitId), [run.items]);

  const pickerOptions = (item: EvvRunItem): Array<{ visitId: string; label: string }> => {
    if (item.bucket === 'MANUAL_REVIEW' && item.candidateVisitIds?.length) {
      return item.candidateVisitIds.map((visitId) => ({ visitId, label: `Visit ${visitId}` }));
    }
    return peloraOnly
      .filter((p) => !item.patientId || p.patientId === item.patientId)
      .map((p) => ({ visitId: p.peloraVisitId as string, label: `${p.visitDate || ''} · ${formatMinutes(p.peloraMinutes)} · visit ${p.peloraVisitId}` }));
  };

  const resolve = async (item: EvvRunItem, action: 'EXCEPTION' | 'LINKED' | 'DOCUMENTATION_GAP') => {
    const itemReason = reason[item.itemId] || '';
    const itemNote = (note[item.itemId] || '').trim();
    if (action === 'EXCEPTION' && !itemReason) {
      setErrors((prev) => ({ ...prev, [item.itemId]: 'Choose a reason.' }));
      return;
    }
    if (action === 'EXCEPTION' && itemReason === 'OTHER' && !itemNote) {
      setErrors((prev) => ({ ...prev, [item.itemId]: 'A note is required when the reason is Other.' }));
      return;
    }
    if (action === 'LINKED' && !linkVisit[item.itemId]) {
      setErrors((prev) => ({ ...prev, [item.itemId]: 'Pick the Pelora visit to link.' }));
      return;
    }
    setBusy((prev) => ({ ...prev, [item.itemId]: true }));
    setErrors((prev) => ({ ...prev, [item.itemId]: '' }));
    try {
      await EvvReconciliationService.resolveItem(run.reconciliationId, item.itemId, {
        action,
        reasonCode: action === 'EXCEPTION' ? (itemReason as EvvExceptionReason) : undefined,
        note: itemNote || undefined,
        peloraVisitId: action === 'LINKED' ? linkVisit[item.itemId] : undefined,
      });
      onChanged(action === 'EXCEPTION' ? 'EVV exception saved' : action === 'LINKED' ? 'HHAX row linked; the visit is verified' : 'Marked as a documentation gap');
    } catch (err) {
      setErrors((prev) => ({ ...prev, [item.itemId]: errorMessage(err, 'Could not save') }));
    } finally {
      setBusy((prev) => ({ ...prev, [item.itemId]: false }));
    }
  };

  return (
    <div className="space-y-3">
      {open.length === 0 ? (
        <p className="text-sm text-emerald-700">Nothing needs a decision in this run.</p>
      ) : (
        open.map((item) => (
          <div key={item.itemId} className="rounded-md border border-slate-200 p-3" data-testid={`evv-item-${item.bucket}`}>
            <p className="text-sm font-medium text-slate-800">{bucketLabel(item.bucket)}</p>
            {item.peloraVisitId && (
              <p className="text-xs text-slate-600">
                Pelora visit {item.peloraVisitId}{item.visitDate ? ` · ${item.visitDate}` : ''}{item.peloraMinutes ? ` · ${formatMinutes(item.peloraMinutes)}` : ''} · EVV Pending
              </p>
            )}
            <HhaxSummary item={item} />
            {(item.unresolvedReason || item.reviewReason) && (
              <p className="mt-1 text-xs text-amber-700">{REASON_TEXT[item.unresolvedReason || item.reviewReason || ''] || ''}</p>
            )}

            {canWrite && item.bucket === 'BLACKBIRD_ONLY' && (
              <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
                <select
                  aria-label="Exception reason"
                  className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                  value={reason[item.itemId] || ''}
                  onChange={(e) => setReason((prev) => ({ ...prev, [item.itemId]: e.target.value as EvvExceptionReason }))}
                >
                  <option value="">Choose a reason</option>
                  {EXCEPTION_REASONS.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => resolve(item, 'EXCEPTION')}
                  disabled={busy[item.itemId]}
                  className="rounded-md bg-slate-800 px-3 py-1.5 text-sm text-white hover:bg-slate-700 disabled:opacity-50"
                >
                  Set EVV exception
                </button>
              </div>
            )}

            {canWrite && (item.bucket === 'HHAX_ONLY' || item.bucket === 'MANUAL_REVIEW') && (
              <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-3">
                <select
                  aria-label="Pelora visit to link"
                  className="rounded-md border border-slate-300 px-2 py-1.5 text-sm md:col-span-2"
                  value={linkVisit[item.itemId] || ''}
                  onChange={(e) => setLinkVisit((prev) => ({ ...prev, [item.itemId]: e.target.value }))}
                >
                  <option value="">{pickerOptions(item).length ? 'Pick the Pelora visit' : 'No unmatched Pelora visit in this run'}</option>
                  {pickerOptions(item).map((o) => (
                    <option key={o.visitId} value={o.visitId}>{o.label}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => resolve(item, 'LINKED')}
                  disabled={busy[item.itemId] || !linkVisit[item.itemId]}
                  className="rounded-md bg-slate-800 px-3 py-1.5 text-sm text-white hover:bg-slate-700 disabled:opacity-50"
                >
                  Link visit
                </button>
                <button
                  type="button"
                  onClick={() => resolve(item, 'DOCUMENTATION_GAP')}
                  disabled={busy[item.itemId]}
                  className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50 md:col-span-3"
                >
                  Mark documentation gap (no Pelora visit is created)
                </button>
              </div>
            )}

            {canWrite && (
              <textarea
                className="mt-2 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                rows={2}
                maxLength={500}
                placeholder="Note (required for Other)"
                value={note[item.itemId] || ''}
                onChange={(e) => setNote((prev) => ({ ...prev, [item.itemId]: e.target.value }))}
              />
            )}
            {errors[item.itemId] && <p className="mt-1 text-xs text-rose-700" role="alert">{errors[item.itemId]}</p>}
            {canOverride && item.bucket === 'BLACKBIRD_ONLY' && item.patientId && item.peloraVisitId && (
              <div className="mt-2 flex justify-end">
                <EvvOverrideForm patientId={item.patientId} visitId={item.peloraVisitId} peloraMinutes={item.peloraMinutes} onDone={onChanged} />
              </div>
            )}
          </div>
        ))
      )}

      {matched.length > 0 && (
        <div className="rounded-md border border-slate-200 p-3">
          <button type="button" className="text-sm font-medium text-slate-700" onClick={() => setShowMatched((v) => !v)}>
            {showMatched ? 'Hide' : 'Show'} matched visits ({matched.length})
          </button>
          {showMatched && (
            <ul className="mt-2 space-y-2">
              {matched.map((item) => (
                <li key={item.itemId} className="rounded border border-slate-100 bg-slate-50 p-2 text-xs text-slate-700">
                  <span className="font-medium">{bucketLabel(item.bucket)}</span> · visit {item.peloraVisitId}
                  {' · '}Pelora {formatMinutes(item.peloraMinutes)} · HHAX {formatMinutes(item.hhaxMinutes)}
                  {item.evvVerifiedMinutes !== undefined ? ` · billable ${formatMinutes(item.evvVerifiedMinutes)}` : ''}
                  {item.upgradedFrom ? ' · exception cleared by this run' : ''}
                  <HhaxSummary item={item} />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default EvvRunItems;
